import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { ImageSquare, FunnelSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';

const DAMAGE_TYPES = [
  'Semua', 'Pemisahan Tapak', 'Meledak', 'Jalan Kempes', 'Sobek/Bocor',
  'Kerusakan Dinding', 'Kerusakan Bead', 'Kerusakan Kimia', 'Lainnya',
];

const PLACEHOLDER_IMAGES = [
  'https://images.unsplash.com/photo-1477823986828-5aff156284aa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwyfHxkYW1hZ2VkJTIwdGlyZSUyMHNjcmFwfGVufDB8fHx8MTc3OTE3MzM3N3ww&ixlib=rb-4.1.0&q=85',
  'https://images.pexels.com/photos/6156594/pexels-photo-6156594.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  'https://images.pexels.com/photos/16008763/pexels-photo-16008763.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
];

export default function ScrapGallery() {
  const { profile } = useAuth();
  const [scraps, setScraps] = useState([]);
  const [filterDamage, setFilterDamage] = useState('Semua');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');

  useEffect(() => { if (profile) fetchScraps(); }, [profile]);

  const fetchScraps = async () => {
    try {
      const { data, error } = await supabase
        .from('scrap_records')
        .select('*, tires!inner (id, serial_number, type, size, company_id)')
        .eq('tires.company_id', profile.company_id)
        .order('scrapped_at', { ascending: false });
      if (error) throw error;
      setScraps(data || []);
    } catch (error) {
      console.error('Error fetching scraps:', error);
      toast.error('Gagal memuat catatan ban rusak');
    }
  };

  const filteredScraps = scraps.filter(scrap => {
    if (filterDamage !== 'Semua' && !scrap.damage_types?.includes(filterDamage)) return false;
    if (filterStartDate && new Date(scrap.scrapped_at) < new Date(filterStartDate)) return false;
    if (filterEndDate) {
      const endOfDay = new Date(filterEndDate);
      endOfDay.setHours(23, 59, 59, 999);
      if (new Date(scrap.scrapped_at) > endOfDay) return false;
    }
    return true;
  });

  const getImage = (scrap, index) => {
    if (scrap.photo_url && scrap.photo_url.startsWith('http')) return scrap.photo_url;
    return PLACEHOLDER_IMAGES[index % PLACEHOLDER_IMAGES.length];
  };

  return (
    <div className="p-4 sm:p-8" data-testid="scrap-gallery-page">
      <h1 className="text-3xl sm:text-4xl font-black uppercase tracking-widest text-zinc-900 mb-8" style={{ fontFamily: 'Chivo' }}>
        GALERI BAN RUSAK
      </h1>

      <div className="bg-white border border-zinc-200 p-6 mb-8 shadow-sm" data-testid="filters">
        <div className="flex items-center gap-2 mb-4">
          <FunnelSimple size={16} weight="bold" className="text-zinc-500" />
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500">FILTER</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 block mb-2">Jenis Kerusakan</label>
            <select value={filterDamage} onChange={(e) => setFilterDamage(e.target.value)}
              className="w-full bg-white border border-zinc-300 p-3 text-zinc-900 font-mono focus:border-red-500 focus:ring-1 focus:ring-red-500 focus:outline-none"
              data-testid="filter-damage-select">
              {DAMAGE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 block mb-2">Tanggal Mulai</label>
            <input type="date" value={filterStartDate} onChange={(e) => setFilterStartDate(e.target.value)}
              className="w-full bg-white border border-zinc-300 p-3 text-zinc-900 font-mono focus:border-red-500 focus:ring-1 focus:ring-red-500 focus:outline-none"
              data-testid="filter-start-date" />
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 block mb-2">Tanggal Akhir</label>
            <input type="date" value={filterEndDate} onChange={(e) => setFilterEndDate(e.target.value)}
              className="w-full bg-white border border-zinc-300 p-3 text-zinc-900 font-mono focus:border-red-500 focus:ring-1 focus:ring-red-500 focus:outline-none"
              data-testid="filter-end-date" />
          </div>
        </div>
      </div>

      {filteredScraps.length === 0 ? (
        <div className="text-center py-20">
          <ImageSquare size={64} weight="bold" className="text-zinc-300 mx-auto mb-4" />
          <p className="text-zinc-500 font-mono text-sm">Tidak ada catatan ban rusak</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="scrap-grid">
          {filteredScraps.map((scrap, idx) => (
            <div key={scrap.id} className="bg-white border border-zinc-200 overflow-hidden shadow-sm" data-testid={`scrap-card-${scrap.tires.serial_number}`}>
              <div className="aspect-square relative overflow-hidden bg-zinc-100">
                <img src={getImage(scrap, idx)} alt={`Scrap ${scrap.tires.serial_number}`}
                  className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all" />
                {scrap.damage_types?.[0] && (
                  <div className="absolute top-2 left-2 bg-red-600 text-white px-2 py-1 font-mono text-[10px] uppercase tracking-wider">
                    {scrap.damage_types[0]}
                  </div>
                )}
              </div>
              <div className="p-4">
                <p className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 mb-1">NOMOR SERI</p>
                <p className="text-sm text-zinc-900 font-mono font-bold mb-2">{scrap.tires.serial_number}</p>
                <div className="grid grid-cols-2 gap-2 text-xs font-mono">
                  <div>
                    <p className="text-zinc-500 uppercase tracking-wider">Ukuran</p>
                    <p className="text-zinc-700">{scrap.tires.size}</p>
                  </div>
                  <div>
                    <p className="text-zinc-500 uppercase tracking-wider">KM Berjalan</p>
                    <p className="text-zinc-700">{scrap.km_total?.toLocaleString('id-ID') || 0}</p>
                  </div>
                  <div>
                    <p className="text-zinc-500 uppercase tracking-wider">Tapak</p>
                    <p className="text-zinc-700">{scrap.tread_depth}mm</p>
                  </div>
                  <div>
                    <p className="text-zinc-500 uppercase tracking-wider">Tanggal</p>
                    <p className="text-zinc-700">{new Date(scrap.scrapped_at).toLocaleDateString('id-ID')}</p>
                  </div>
                </div>
                {scrap.damage_types?.length > 1 && (
                  <div className="mt-3 pt-3 border-t border-zinc-200">
                    <p className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">+ {scrap.damage_types.length - 1} jenis kerusakan lain</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
